import { useState, useEffect, useRef, useMemo } from "react";
import Papa from "papaparse";
import {
  ENVIO_OPTIONS,
  POLAROID_PACK_PRICE,
  FLOOR_WORD_LIMIT,
  countWords,
  computeTotal,
  formatArs,
} from "./pricing";
import { QUOTES, THEME_LABELS, THEME_KEYWORDS } from "./quotes";

// Número de WhatsApp para mandar comprobantes de transferencia. Los datos
// bancarios (CBU/alias/titular) vienen del servidor, no de acá.
const WHATSAPP_NUMBER = "541130489994";

// ---------------------------------------------------------------------------
// Agustín: pegá acá la URL de tu Google Sheet publicada como CSV. Mientras
// esté vacía, la app usa la biblioteca de citas de ./quotes.js (podés partir
// de docs/citas.csv: importalo en la planilla y editalo ahí).
//
// Cómo conseguir la URL:
// 1. En tu Sheet: Archivo → Compartir → Publicar en la Web.
// 2. Elegí la hoja (no "todo el documento") y el formato "Valores separados
//    por comas (.csv)". Publicar.
// 3. Copiá el link que te da y pegalo entre las comillas de acá abajo.
//
// Columnas que tiene que tener la hoja (nombres exactos en la fila 1):
//   tema | label | palabras_clave | cita | autor | obra
// - tema: identificador simple, ej: amor, agradecimiento, amistad
// - label: cómo se muestra en la app, ej: Amor, Agradecimiento, Amistad
// - palabras_clave: separadas por coma, alcanza con completarlas en UNA fila
//   por tema (ej: amor, enamorado, pareja, novio, novia, corazón)
// - cita / autor: el texto y quién lo dijo
// - obra (opcional): de dónde sale la cita, ej: Rima XXI
// ---------------------------------------------------------------------------
const SHEET_CSV_URL = "";

const FALLBACK_QUOTES = QUOTES;
const FALLBACK_THEME_LABELS = THEME_LABELS;
const FALLBACK_THEME_KEYWORDS = THEME_KEYWORDS;

function normalize(s) {
  return s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function detectTheme(topic, themeKeywords) {
  const t = normalize(topic || "");
  if (!t.trim()) return null;
  for (const [theme, words] of Object.entries(themeKeywords)) {
    if (words.some((w) => t.includes(normalize(w)))) return theme;
  }
  return null;
}

// Convierte las filas del CSV en la misma forma que usa la app: citas
// agrupadas por tema, labels por tema, y palabras clave por tema.
function parseQuoteRows(rows) {
  const quotes = {};
  const labels = {};
  const keywords = {};
  rows.forEach((row) => {
    const tema = (row.tema || "").trim().toLowerCase();
    const cita = (row.cita || "").trim();
    if (!tema || !cita) return;
    if (!quotes[tema]) quotes[tema] = [];
    quotes[tema].push({ text: cita, author: (row.autor || "").trim(), source: (row.obra || "").trim() });
    if (!labels[tema] && row.label) labels[tema] = row.label.trim();
    if (!keywords[tema] && row.palabras_clave) {
      keywords[tema] = row.palabras_clave
        .split(",")
        .map((k) => k.trim())
        .filter(Boolean);
    }
  });
  return { quotes, labels, keywords };
}

// Trae y parsea la planilla una sola vez. Si no hay URL configurada, o si
// falla la carga, se queda con null y el componente usa el respaldo.
function useQuoteLibrary(url) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!url) return;
    Papa.parse(url, {
      download: true,
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const parsed = parseQuoteRows(results.data);
        if (Object.keys(parsed.quotes).length > 0) setData(parsed);
      },
      error: () => {
        // Si falla la carga (planilla no publicada, sin internet, etc.),
        // seguimos con las citas de respaldo sin romper la app.
      },
    });
  }, [url]);
  return data;
}

function shade(hex, percent) {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.min(255, Math.max(0, ((n >> 16) & 255) + percent));
  const g = Math.min(255, Math.max(0, ((n >> 8) & 255) + percent));
  const b = Math.min(255, Math.max(0, (n & 255) + percent));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

// Genera un círculo con ondulaciones suaves y uniformes (borde de sello de lacre).
function wavyCirclePath(cx, cy, r, waves, amp) {
  const segments = 96;
  let d = "";
  for (let i = 0; i <= segments; i++) {
    const t = (i / segments) * Math.PI * 2;
    const rad = r + amp * Math.sin(t * waves);
    const x = cx + rad * Math.cos(t);
    const y = cy + rad * Math.sin(t);
    d += (i === 0 ? "M" : "L") + x.toFixed(2) + "," + y.toFixed(2) + " ";
  }
  return d + "Z";
}

function perforationPoints(w, h, step) {
  const points = [];
  const halfW = w / 2;
  const halfH = h / 2;
  for (let x = -halfW + step / 2; x < halfW; x += step) {
    points.push([x, -halfH]);
    points.push([x, halfH]);
  }
  for (let y = -halfH + step / 2; y < halfH; y += step) {
    points.push([-halfW, y]);
    points.push([halfW, y]);
  }
  return points;
}

// Paleta real de "Color de sobre" tomada del menú de That Letter en Instagram.
// (Constantes y funciones de precio viven en ./pricing.js — se comparten con
// el backend, que nunca debe confiar en precios calculados por el cliente.)
const ENVELOPE_COLORS = [
  { id: "verde-ingles", label: "Verde Inglés", hex: "#3C5C40" },
  { id: "rojo", label: "Rojo", hex: "#B32026" },
  { id: "bordo", label: "Bordó", hex: "#6E1B26" },
  { id: "celeste", label: "Celeste", hex: "#4FC1E0" },
  { id: "lila", label: "Lila", hex: "#C9A3E8" },
  { id: "violeta", label: "Violeta", hex: "#5A2C86" },
  { id: "negro", label: "Negro", hex: "#1C1A18" },
  { id: "azul-marino", label: "Azul Marino", hex: "#1F2A4A" },
  { id: "blanco", label: "Blanco", hex: "#FBF8F1" },
];

const CINTA_COLORS = [
  { id: "marfil", label: "Marfil", hex: "#EDE4D3" },
  { id: "bordo-hilo", label: "Bordó", hex: "#5B2333" },
  { id: "hilo-dorado", label: "Dorado", hex: "#B8963E" },
];

// El sello de lacre no es elegible por color: siempre cera roja con monograma TL.
const WAX_COLOR = "#7A2A1D";
const FLOR_SECA_COLOR = "#C7935A";
const LAZO_COLOR = "#D8C79A";
const STAMP_COLOR = "#8C4A32";

const INK = "#2E2013";
const INK_MUTED = "#4A3B22";
const PLACEHOLDER_COLOR = "#5C4A2C";
const PARCHMENT_TEXT = "#F1E8D2";
const LETTERHEAD_COLOR = "#4A3B22";
const KRAFT_BASE = "#F3EEDD";
const BOX_LIGHT = shade(KRAFT_BASE, -16);
const VINTAGE_FONT = "'Times New Roman', Times, Georgia, serif";
const TYPEWRITER_PHRASE = "Empezá a escribir tu carta...";

const paperNoiseUri = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='3' stitchTiles='stitch'/><feColorMatrix type='matrix' values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.35 0'/></filter><rect width='100%' height='100%' filter='url(#n)'/></svg>`
)}`;

// Efecto de "tipeo" para el placeholder del editor: escribe y borra en loop.
function useTypewriterPlaceholder(active, phrase) {
  const [text, setText] = useState("");
  useEffect(() => {
    if (!active) {
      setText("");
      return;
    }
    let i = 0;
    let phase = "typing";
    let pause = 0;
    let blink = true;
    const id = setInterval(() => {
      if (phase === "typing") {
        i += 1;
        setText(phrase.slice(0, i) + "▍");
        if (i >= phrase.length) {
          phase = "pauseFull";
          pause = 0;
        }
      } else if (phase === "pauseFull") {
        pause += 1;
        blink = !blink;
        setText(phrase + (blink ? "▍" : ""));
        if (pause > 14) phase = "deleting";
      } else if (phase === "deleting") {
        i -= 1;
        setText(phrase.slice(0, i) + (i > 0 ? "▍" : ""));
        if (i <= 0) {
          phase = "pauseEmpty";
          pause = 0;
        }
      } else {
        pause += 1;
        setText("");
        if (pause > 6) {
          phase = "typing";
          i = 0;
        }
      }
    }, 90);
    return () => clearInterval(id);
  }, [active, phrase]);
  return text;
}

// Dictado: graba con el micrófono, manda el audio a /api/transcribir (Gemini)
// y entrega el texto a onText. Se corta solo a los MAX_DICTATION_SECONDS.
const MAX_DICTATION_SECONDS = 5 * 60;
const DICTATION_SUPPORTED =
  typeof window !== "undefined" && !!navigator.mediaDevices?.getUserMedia && typeof window.MediaRecorder !== "undefined";

function pickRecordingMimeType() {
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"];
  return candidates.find((t) => MediaRecorder.isTypeSupported?.(t)) || "";
}

// Gemini no lista webm (Chrome) ni mp4 (Safari) entre sus formatos de audio,
// así que la grabación se decodifica y se re-codifica como WAV mono de 16 kHz,
// que alcanza para voz. Devuelve el WAV en base64.
const DICTATION_SAMPLE_RATE = 16000;

async function recordingToWavBase64(blob) {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const ctx = new AudioCtx();
  let decoded;
  try {
    decoded = await ctx.decodeAudioData(await blob.arrayBuffer());
  } finally {
    ctx.close();
  }
  const length = Math.ceil(decoded.duration * DICTATION_SAMPLE_RATE);
  const offline = new OfflineAudioContext(1, length, DICTATION_SAMPLE_RATE);
  const source = offline.createBufferSource();
  source.buffer = decoded;
  source.connect(offline.destination);
  source.start();
  const samples = (await offline.startRendering()).getChannelData(0);

  const view = new DataView(new ArrayBuffer(44 + samples.length * 2));
  const writeAscii = (offset, s) => [...s].forEach((c, i) => view.setUint8(offset + i, c.charCodeAt(0)));
  writeAscii(0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  writeAscii(8, "WAVE");
  writeAscii(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, DICTATION_SAMPLE_RATE, true);
  view.setUint32(28, DICTATION_SAMPLE_RATE * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeAscii(36, "data");
  view.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }

  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(new Blob([view.buffer], { type: "audio/wav" }));
  });
  return dataUrl.slice(dataUrl.indexOf(",") + 1);
}

function useDictation(onText) {
  const [status, setStatus] = useState("idle"); // idle | recording | transcribing | error | denied | empty
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  function releaseMic() {
    clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }

  useEffect(() => () => {
    if (recorderRef.current?.state === "recording") {
      recorderRef.current.onstop = null;
      recorderRef.current.stop();
    }
    releaseMic();
  }, []);

  async function transcribe(blob) {
    setStatus("transcribing");
    try {
      const res = await fetch("/api/transcribir", {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: await recordingToWavBase64(blob),
      });
      if (!res.ok) throw new Error("transcription failed");
      const { texto } = await res.json();
      if (!texto) {
        setStatus("empty");
        return;
      }
      onTextRef.current(texto);
      setStatus("idle");
    } catch {
      setStatus("error");
    }
  }

  async function start() {
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setStatus("denied");
      return;
    }
    streamRef.current = stream;
    const mimeType = pickRecordingMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType, audioBitsPerSecond: 32000 } : undefined);
    const chunks = [];
    recorder.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
    recorder.onstop = () => {
      releaseMic();
      const blob = new Blob(chunks, { type: recorder.mimeType || mimeType });
      if (blob.size === 0) {
        setStatus("empty");
        return;
      }
      transcribe(blob);
    };
    recorderRef.current = recorder;
    recorder.start(1000);
    setSeconds(0);
    setStatus("recording");
    const startedAt = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setSeconds(elapsed);
      if (elapsed >= MAX_DICTATION_SECONDS) stop();
    }, 250);
  }

  function stop() {
    clearInterval(timerRef.current);
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  }

  return { status, seconds, start, stop };
}

function formatClock(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = String(totalSeconds % 60).padStart(2, "0");
  return `${m}:${s}`;
}

export default function LetterEnvelopeConfigurator() {
  const [topic, setTopic] = useState("");
  const [letter, setLetter] = useState("");
  const [fixStatus, setFixStatus] = useState("idle"); // idle | loading | error
  const [fixSuggestion, setFixSuggestion] = useState(null);
  const [envelopeColorId, setEnvelopeColorId] = useState("blanco");
  const [closureType, setClosureType] = useState("lacre");
  const [closureColorId, setClosureColorId] = useState("marfil");
  const [florSeca, setFlorSeca] = useState(false);
  const [lazo, setLazo] = useState(false);
  const [estampillas, setEstampillas] = useState(false);
  const [dedication, setDedication] = useState("");
  const [envioId, setEnvioId] = useState(ENVIO_OPTIONS[0].id);
  const [envioStation, setEnvioStation] = useState("");
  const [polaroidPacks, setPolaroidPacks] = useState(0);
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [step, setStep] = useState(0);
  const [customerName, setCustomerName] = useState("");
  const [customerContact, setCustomerContact] = useState("");
  const [submitStatus, setSubmitStatus] = useState("idle");
  const [paymentMethod, setPaymentMethod] = useState("mercadopago");
  const [transferOrder, setTransferOrder] = useState(null); // { orderId, datosBancarios }
  const [comprobanteStatus, setComprobanteStatus] = useState("idle");
  const [postPagoStatus, setPostPagoStatus] = useState(null); // "exito" | "fallo" | "pendiente"
  const STEP_COUNT = 4;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  // Al volver de Mercado Pago, el sitio recarga por completo (se pierde el
  // estado del wizard) y llega con ?pago=exito|fallo|pendiente en la URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pago = params.get("pago");
    if (pago) {
      setPostPagoStatus(pago);
      // Mercado Pago agrega payment_id a la URL de retorno. El servidor lo
      // verifica contra la API de MP (no confía en lo que mande el navegador),
      // así el pedido se marca pagado aunque el webhook no llegue.
      const orderId = params.get("orderId");
      const paymentId = params.get("payment_id");
      if (pago === "exito" && orderId && paymentId) {
        fetch("/api/confirmar-pago", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ orderId, paymentId }),
        }).catch(() => {});
      }
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  function goNext() {
    setStep((s) => Math.min(STEP_COUNT - 1, s + 1));
  }
  function goPrev() {
    setStep((s) => Math.max(0, s - 1));
  }

  const isTypingRef = useRef(false);
  const typingTimeoutRef = useRef(null);
  const typedPlaceholder = useTypewriterPlaceholder(letter.length === 0, TYPEWRITER_PHRASE);

  const sheetLibrary = useQuoteLibrary(SHEET_CSV_URL);
  const activeQuotes = sheetLibrary?.quotes || FALLBACK_QUOTES;
  const activeLabels = sheetLibrary?.labels || FALLBACK_THEME_LABELS;
  const activeKeywords = sheetLibrary?.keywords || FALLBACK_THEME_KEYWORDS;

  const theme = useMemo(() => detectTheme(topic, activeKeywords), [topic, activeKeywords]);
  const quotePool = theme ? activeQuotes[theme] || [] : Object.values(activeQuotes).flat();
  const wordCount = useMemo(() => countWords(letter), [letter]);
  const pricing = useMemo(
    () => computeTotal({ wordCount, envioId, polaroidPacks }),
    [wordCount, envioId, polaroidPacks]
  );
  const { estimatedPrice, envioOption, polaroidPrice, discountAmount, totalPrice } = pricing;

  useEffect(() => {
    setQuoteIndex(0);
  }, [theme]);

  useEffect(() => {
    const id = setInterval(() => {
      if (!isTypingRef.current) {
        setQuoteIndex((i) => (i + 1) % quotePool.length);
      }
    }, 12000);
    return () => clearInterval(id);
  }, [quotePool.length]);

  const dictation = useDictation((texto) => {
    setFixSuggestion(null);
    setLetter((prev) => (prev.trim() ? `${prev.trimEnd()}\n\n${texto}` : texto));
  });

  async function requestSpellFix() {
    setFixStatus("loading");
    setFixSuggestion(null);
    try {
      const res = await fetch("/api/corregir", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ texto: letter }),
      });
      if (!res.ok) throw new Error("fix failed");
      const { corregido } = await res.json();
      setFixSuggestion(corregido === letter.trim() ? "" : corregido);
      setFixStatus("idle");
    } catch {
      setFixStatus("error");
    }
  }

  function handleLetterChange(e) {
    setFixSuggestion(null);
    if (fixStatus === "error") setFixStatus("idle");
    setLetter(e.target.value);
    isTypingRef.current = true;
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      isTypingRef.current = false;
    }, 1800);
  }

  const envelopeColor = ENVELOPE_COLORS.find((c) => c.id === envelopeColorId) || ENVELOPE_COLORS[0];
  const closureColor = closureType === "cinta" ? CINTA_COLORS.find((c) => c.id === closureColorId) || CINTA_COLORS[0] : null;

  function selectClosureType(type) {
    setClosureType(type);
    if (type === "cinta") setClosureColorId(CINTA_COLORS[0].id);
  }

  async function handleSubmitOrder() {
    if (!customerName.trim() || !customerContact.trim()) {
      setSubmitStatus("missing");
      return;
    }
    setSubmitStatus("sending");
    try {
      // Los precios viajan como referencia para el email/UI, pero el
      // servidor los recalcula desde cero (envioId, wordCount, polaroidPacks)
      // y nunca confía en estos valores para cobrar.
      const payload = {
        nombre: customerName,
        contacto: customerContact,
        carta: letter,
        envioId,
        entregaEstacion: envioId === "estacion-urquiza" ? envioStation : "",
        colorSobre: envelopeColor.label,
        cierre: closureType === "lacre" ? "Sello en lacre" : `Cinta con moñito (${closureColor ? closureColor.label : ""})`,
        florSeca: florSeca && lacreDetailsEnabled,
        lazo: lazo && lacreDetailsEnabled,
        estampillas,
        polaroidPacks,
        dedicatoria: dedication,
        metodoPago: paymentMethod,
      };
      const res = await fetch("/api/crear-pedido", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("fallo el envío");
      const data = await res.json();
      if (paymentMethod === "mercadopago") {
        sessionStorage.setItem("tl_contacto", customerContact);
        window.location.href = data.redirectUrl;
        return;
      }
      setTransferOrder({ orderId: data.orderId, datosBancarios: data.datosBancarios });
      setSubmitStatus("sent");
    } catch (err) {
      setSubmitStatus("error");
    }
  }

  async function handleUploadComprobante(file) {
    if (!file || !transferOrder) return;
    setComprobanteStatus("uploading");
    try {
      const formData = new FormData();
      formData.append("orderId", transferOrder.orderId);
      formData.append("comprobante", file);
      const res = await fetch("/api/subir-comprobante", { method: "POST", body: formData });
      if (!res.ok) throw new Error("fallo la subida");
      setComprobanteStatus("uploaded");
    } catch (err) {
      setComprobanteStatus("error");
    }
  }

  const quote = quotePool[quoteIndex % quotePool.length];
  const lacreDetailsEnabled = closureType === "lacre";

  return (
    <div style={{ position: "relative", minHeight: "100vh", backgroundColor: KRAFT_BASE }}>
      <style>{`
        .tl-field::placeholder { color: ${PLACEHOLDER_COLOR}; opacity: 1; }
        .tl-underline:focus { border-bottom-color: ${INK} !important; }
        .tl-box:focus { box-shadow: 0 1px 4px rgba(0,0,0,0.22) !important; }
        @keyframes tl-blink { 50% { opacity: 0.25; } }
        .tl-rec-dot { animation: tl-blink 1.1s ease-in-out infinite; }
      `}</style>

      <div
        style={{ position: "absolute", inset: 0, backgroundImage: `url("${paperNoiseUri}")`, mixBlendMode: "multiply", opacity: 0.35, pointerEvents: "none" }}
      />

      <div
        style={{
          position: "relative",
          fontFamily: VINTAGE_FONT,
          color: INK,
          maxWidth: 480,
          margin: "0 auto",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          padding: "26px 20px 30px",
          boxSizing: "border-box",
        }}
      >
        <div>
          <LetterheadRow left="That Letter" right="San Miguel, Buenos Aires" />
          {!postPagoStatus && <StepDots step={step} total={STEP_COUNT} />}
        </div>

        {postPagoStatus ? (
          <PostPagoScreen status={postPagoStatus} />
        ) : (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          {step === 0 && (
            <>
              <h1 style={{ textAlign: "center", fontSize: 27, fontWeight: 700, margin: "22px 0 4px", letterSpacing: 0.3 }}>Empecemos</h1>
              <Flourish />
              <p style={{ textAlign: "center", fontSize: 18, color: INK_MUTED, fontStyle: "italic", margin: "0 0 24px", lineHeight: 1.5 }}>
                Vamos a armar tu carta en unos pocos pasos: primero cómo la coordinamos, después la escribís, y por último elegís el sobre.
              </p>
              <p style={{ textAlign: "center", fontSize: 13, fontWeight: 700, margin: "0 0 14px" }}>¿Dónde coordinamos la entrega?</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, maxWidth: 340, margin: "0 auto" }}>
                {ENVIO_OPTIONS.map((opt) => (
                  <EnvioChip
                    key={opt.id}
                    active={envioId === opt.id}
                    onClick={() => setEnvioId(opt.id)}
                    label={opt.label}
                    price={opt.price}
                    discountPercent={opt.discountPercent}
                  />
                ))}
              </div>
              {envioId === "estacion-urquiza" && (
                <div style={{ maxWidth: 280, margin: "14px auto 0" }}>
                  <input
                    className="tl-field tl-underline"
                    value={envioStation}
                    onChange={(e) => setEnvioStation(e.target.value)}
                    placeholder="¿En qué estación te queda mejor?"
                    style={{ ...underlineInputStyle, textAlign: "center", marginTop: 0, fontSize: 13 }}
                  />
                </div>
              )}
            </>
          )}

        {step === 1 && (
          <>
            <h1 style={{ textAlign: "center", fontSize: 27, fontWeight: 700, margin: "22px 0 4px", letterSpacing: 0.3 }}>Armemos tu carta</h1>
            <Flourish />

            <Section title="¿Para quién es y qué querés contar?">
              <input
                className="tl-field tl-underline"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="ej: quiero agradecerle a mi abuela..."
                style={underlineInputStyle}
              />
            </Section>

            <div
              style={{
                border: `1px solid ${INK_MUTED}88`,
                borderRadius: 2,
                padding: "14px 16px",
                margin: "22px 0",
                background: "rgba(0,0,0,0.035)",
              }}
            >
              <p style={{ fontFamily: VINTAGE_FONT, fontStyle: "italic", fontSize: 18, color: INK_MUTED, margin: 0 }}>
                {theme ? `Inspiración · ${activeLabels[theme] || theme}` : "Inspiración"}
              </p>
              <p style={{ margin: "8px 0 4px", fontSize: 16, lineHeight: 1.5, fontStyle: "italic" }}>&ldquo;{quote.text}&rdquo;</p>
              <p style={{ margin: 0, fontSize: 13, color: INK_MUTED }}>
                — {quote.author}
                {quote.source && <span style={{ fontStyle: "italic" }}>, {quote.source}</span>}
              </p>
              {!theme && (
                <p style={{ margin: "10px 0 0", fontSize: 18, color: INK_MUTED, opacity: 0.9, fontStyle: "italic", textAlign: "center", lineHeight: 1.5 }}>
                  Contanos qué querés escribir arriba y las citas se van a ajustar al tema.
                </p>
              )}
            </div>

            <Section title="Tu carta">
              {DICTATION_SUPPORTED && (
                <DictationControl
                  status={dictation.status}
                  seconds={dictation.seconds}
                  onStart={dictation.start}
                  onStop={dictation.stop}
                />
              )}
              <textarea
                className="tl-field tl-box"
                value={letter}
                onChange={handleLetterChange}
                rows={8}
                placeholder={typedPlaceholder}
                style={{
                  width: "100%",
                  marginTop: 8,
                  padding: "12px",
                  border: "none",
                  borderRadius: 6,
                  background: BOX_LIGHT,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.16)",
                  fontFamily: VINTAGE_FONT,
                  fontSize: 15,
                  lineHeight: 1.7,
                  outline: "none",
                  boxSizing: "border-box",
                  color: INK,
                  resize: "vertical",
                }}
              />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 8, padding: "0 2px" }}>
                <span style={{ fontSize: 12.5, color: INK_MUTED, fontStyle: "italic" }}>
                  {wordCount} {wordCount === 1 ? "palabra" : "palabras"}
                </span>
                <span style={{ fontSize: 15, fontWeight: 700 }}>
                  ${formatArs(estimatedPrice)}
                  {wordCount <= FLOOR_WORD_LIMIT && (
                    <span style={{ fontSize: 11, fontWeight: 400, fontStyle: "italic", color: INK_MUTED }}> (mínimo)</span>
                  )}
                </span>
              </div>

              {letter.trim().length > 0 && fixSuggestion === null && (
                <div style={{ textAlign: "center", marginTop: 12 }}>
                  <button
                    type="button"
                    onClick={requestSpellFix}
                    disabled={fixStatus === "loading"}
                    style={{
                      background: "none",
                      border: `1px solid ${INK_MUTED}88`,
                      borderRadius: 2,
                      padding: "6px 14px",
                      fontFamily: VINTAGE_FONT,
                      fontStyle: "italic",
                      fontSize: 14,
                      color: INK,
                      cursor: fixStatus === "loading" ? "default" : "pointer",
                    }}
                  >
                    {fixStatus === "loading" ? "Revisando…" : "Revisar ortografía"}
                  </button>
                  {fixStatus === "error" && (
                    <p style={{ margin: "8px 0 0", fontSize: 13, color: INK_MUTED, fontStyle: "italic" }}>
                      No pudimos revisar el texto ahora. Podés seguir igual.
                    </p>
                  )}
                </div>
              )}

              {fixSuggestion === "" && (
                <p style={{ textAlign: "center", marginTop: 12, fontSize: 13, color: INK_MUTED, fontStyle: "italic" }}>
                  No encontramos nada para corregir.
                </p>
              )}

              {fixSuggestion && (
                <div style={{ marginTop: 14, padding: "12px 14px", background: "rgba(0,0,0,0.035)", border: `1px solid ${INK_MUTED}88`, borderRadius: 2 }}>
                  <p style={{ margin: "0 0 8px", fontSize: 14, fontStyle: "italic", color: INK_MUTED }}>Versión corregida</p>
                  <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{fixSuggestion}</p>
                  <div style={{ display: "flex", justifyContent: "center", gap: 22, marginTop: 12 }}>
                    <TextToggle
                      active
                      onClick={() => {
                        setLetter(fixSuggestion);
                        setFixSuggestion(null);
                      }}
                    >
                      Aplicar
                    </TextToggle>
                    <TextToggle active={false} onClick={() => setFixSuggestion(null)}>
                      Descartar
                    </TextToggle>
                  </div>
                </div>
              )}
            </Section>
          </>
        )}

        {step === 2 && (
          <>
            <h2 style={{ textAlign: "center", fontSize: 27, fontWeight: 700, margin: "22px 0 22px", letterSpacing: 0.3 }}>Tu sobre</h2>

            <SectionHeader>Color de sobre</SectionHeader>
            <SwatchGrid options={ENVELOPE_COLORS} value={envelopeColorId} onChange={setEnvelopeColorId} />

            <BigSectionHeader>Cierre</BigSectionHeader>
            <div style={{ display: "flex", justifyContent: "center", gap: 22, marginBottom: closureType === "cinta" ? 14 : 6 }}>
              <TextToggle active={closureType === "lacre"} onClick={() => selectClosureType("lacre")}>
                Sello en lacre
              </TextToggle>
              <TextToggle active={closureType === "cinta"} onClick={() => selectClosureType("cinta")}>
                Cinta con moñito
              </TextToggle>
            </div>
            {closureType === "cinta" && <SwatchGrid options={CINTA_COLORS} value={closureColorId} onChange={setClosureColorId} />}

            <div style={{ textAlign: "center", margin: "28px 0 12px" }}>
              <span style={{ fontSize: 19, fontWeight: 700, letterSpacing: 0.2 }}>Detalles de la casa</span>
              {lacreDetailsEnabled && (
                <span style={{ fontSize: 12, fontWeight: 400, fontStyle: "italic", color: INK_MUTED, marginLeft: 6 }}>(sin cargo)</span>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
              <CheckRow
                checked={florSeca && lacreDetailsEnabled}
                disabled={!lacreDetailsEnabled}
                onChange={setFlorSeca}
                label="Flor seca"
                sublabel="(en el lacre)"
              />
              <CheckRow checked={lazo && lacreDetailsEnabled} disabled={!lacreDetailsEnabled} onChange={setLazo} label="Lazo" sublabel="(en el lacre)" />
              <CheckRow checked={estampillas} disabled={false} onChange={setEstampillas} label="Estampillas" />
            </div>
            {!lacreDetailsEnabled && (
              <p style={{ textAlign: "center", fontSize: 18, color: INK_MUTED, fontStyle: "italic", marginTop: 8, lineHeight: 1.5 }}>
                La flor seca y el lazo van sobre el sello de lacre.
              </p>
            )}

            <div style={{ textAlign: "center", margin: "28px 0 4px" }}>
              <span style={{ fontSize: 19, fontWeight: 700, letterSpacing: 0.2 }}>Fotos Polaroid</span>
              <p style={{ fontSize: 18, color: INK_MUTED, fontStyle: "italic", margin: "4px 0 12px" }}>
                ${formatArs(POLAROID_PACK_PRICE)} el paquete de 3 unidades
              </p>
            </div>
            <Stepper value={polaroidPacks} onChange={setPolaroidPacks} />

            <Section title="Dedicatoria en el sobre (opcional)">
              <input
                className="tl-field tl-underline"
                value={dedication}
                onChange={(e) => setDedication(e.target.value.slice(0, 40))}
                placeholder="Para: ..."
                style={underlineInputStyle}
              />
            </Section>

            <div style={{ margin: "10px 0 8px" }}>
              <SectionHeader>Así va a quedar tu sobre</SectionHeader>
              <EnvelopePreview
                envelopeHex={envelopeColor.hex}
                closureType={closureType}
                closureHex={closureColor ? closureColor.hex : null}
                estampillas={estampillas}
                florSeca={florSeca && lacreDetailsEnabled}
                lazo={lazo && lacreDetailsEnabled}
                dedication={dedication}
                polaroidPacks={polaroidPacks}
              />
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <h2 style={{ textAlign: "center", fontSize: 22, fontWeight: 700, margin: "22px 0 4px" }}>Resumen de tu pedido</h2>
            <p style={{ textAlign: "center", fontSize: 18, color: INK_MUTED, fontStyle: "italic", margin: "0 0 16px", lineHeight: 1.5 }}>
              Con esta combinación te vamos a mandar un video mostrándote cómo queda tu carta antes de confirmar.
            </p>

            <div style={{ maxWidth: 340, margin: "0 auto" }}>
              <SummaryRow label="Carta" value={`${wordCount} palabras — $${formatArs(estimatedPrice)}`} />
              <SummaryRow
                label="Entrega"
                value={`${envioOption.label}${envioId === "estacion-urquiza" && envioStation ? ` (${envioStation})` : ""} — ${
                  envioOption.discountPercent > 0
                    ? `${envioOption.discountPercent}% OFF`
                    : envioOption.price === 0
                    ? "Gratis"
                    : `$${formatArs(envioOption.price)}`
                }`}
              />
              <SummaryRow label="Color de sobre" value={envelopeColor.label} />
              <SummaryRow
                label="Cierre"
                value={
                  closureType === "lacre"
                    ? `Sello en lacre${florSeca ? " + flor seca" : ""}${lazo ? " + lazo" : ""}`
                    : `Cinta con moñito (${closureColor.label})`
                }
              />
              {estampillas && <SummaryRow label="Estampillas" value="Sí" />}
              {polaroidPacks > 0 && (
                <SummaryRow label="Fotos Polaroid" value={`${polaroidPacks} paquete${polaroidPacks > 1 ? "s" : ""} — $${formatArs(polaroidPrice)}`} />
              )}
              {dedication.trim() && <SummaryRow label="Dedicatoria" value={`"${dedication}"`} />}
              {discountAmount > 0 && <SummaryRow label={`Descuento (${envioOption.discountPercent}%)`} value={`-$${formatArs(discountAmount)}`} />}
              <div style={{ borderTop: `1.5px solid ${INK}`, marginTop: 10, paddingTop: 10, display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 16, fontWeight: 700 }}>Total estimado</span>
                <span style={{ fontSize: 16, fontWeight: 700 }}>${formatArs(totalPrice)}</span>
              </div>
            </div>

            {submitStatus === "sent" && paymentMethod === "transferencia" && transferOrder ? (
              <TransferPaymentScreen
                datosBancarios={transferOrder.datosBancarios}
                comprobanteStatus={comprobanteStatus}
                onUpload={handleUploadComprobante}
              />
            ) : submitStatus === "sent" ? (
              <p style={{ textAlign: "center", fontSize: 18, fontWeight: 700, margin: "26px 0 0", lineHeight: 1.5 }}>
                ¡Listo! Recibimos tu pedido — te contactamos por {customerContact} a la brevedad.
              </p>
            ) : (
              <>
                <div style={{ textAlign: "center", margin: "26px 0 0" }}>
                  <span style={{ fontSize: 15, fontWeight: 700 }}>¿Cómo preferís pagar?</span>
                  <div style={{ display: "flex", justifyContent: "center", gap: 22, marginTop: 10 }}>
                    <TextToggle active={paymentMethod === "mercadopago"} onClick={() => setPaymentMethod("mercadopago")}>
                      Mercado Pago
                    </TextToggle>
                    <TextToggle active={paymentMethod === "transferencia"} onClick={() => setPaymentMethod("transferencia")}>
                      Transferencia bancaria
                    </TextToggle>
                  </div>
                </div>

                <div style={{ maxWidth: 320, margin: "20px auto 0" }}>
                  <input
                    className="tl-field tl-underline"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Tu nombre"
                    style={underlineInputStyle}
                  />
                  <input
                    className="tl-field tl-underline"
                    value={customerContact}
                    onChange={(e) => setCustomerContact(e.target.value)}
                    placeholder="WhatsApp o email de contacto"
                    style={{ ...underlineInputStyle, marginTop: 14 }}
                  />
                </div>

                {submitStatus === "missing" && (
                  <p style={{ textAlign: "center", fontSize: 13, color: "#8C2F22", fontStyle: "italic", margin: "10px 0 0" }}>
                    Completá tu nombre y un contacto para poder avisarte.
                  </p>
                )}
                {submitStatus === "error" && (
                  <p style={{ textAlign: "center", fontSize: 13, color: "#8C2F22", fontStyle: "italic", margin: "10px 0 0" }}>
                    No se pudo enviar el pedido. Probá de nuevo en un momento.
                  </p>
                )}

                <div style={{ textAlign: "center", marginTop: 20 }}>
                  <button
                    onClick={handleSubmitOrder}
                    disabled={submitStatus === "sending"}
                    style={{
                      background: INK,
                      color: PARCHMENT_TEXT,
                      border: "none",
                      borderRadius: 4,
                      padding: "12px 26px",
                      fontFamily: VINTAGE_FONT,
                      fontSize: 15,
                      fontWeight: 700,
                      cursor: submitStatus === "sending" ? "default" : "pointer",
                      opacity: submitStatus === "sending" ? 0.6 : 1,
                    }}
                  >
                    {submitStatus === "sending"
                      ? "Enviando..."
                      : paymentMethod === "mercadopago"
                      ? "Pagar con Mercado Pago"
                      : "Confirmar pedido"}
                  </button>
                </div>
              </>
            )}
          </>
        )}
        </div>
        )}

        {!postPagoStatus && <StepNav step={step} total={STEP_COUNT} onPrev={goPrev} onNext={goNext} />}
      </div>
    </div>
  );
}

const underlineInputStyle = {
  width: "100%",
  marginTop: 8,
  padding: "6px 2px",
  border: "none",
  borderBottom: `1.5px solid ${INK_MUTED}`,
  background: "transparent",
  fontFamily: VINTAGE_FONT,
  fontSize: 15,
  outline: "none",
  boxSizing: "border-box",
  color: INK,
};

function LetterheadRow({ left, right }) {
  return (
    <p
      style={{
        textAlign: "center",
        fontSize: 10.5,
        letterSpacing: 2.2,
        textTransform: "uppercase",
        color: LETTERHEAD_COLOR,
        fontWeight: 700,
        margin: 0,
      }}
    >
      {left} <span style={{ opacity: 0.7, letterSpacing: 0 }}>·</span> {right}
    </p>
  );
}

function StepDots({ step, total }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, margin: "14px 0 0" }}>
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          style={{
            width: i === step ? 9 : 6,
            height: i === step ? 9 : 6,
            borderRadius: "50%",
            background: i <= step ? INK : INK_MUTED + "40",
            transition: "all 0.15s ease",
          }}
        />
      ))}
    </div>
  );
}

function StepNav({ step, total, onPrev, onNext }) {
  const navBtnStyle = {
    background: "none",
    border: `1.5px solid ${INK}`,
    borderRadius: 4,
    padding: "10px 20px",
    fontFamily: VINTAGE_FONT,
    fontSize: 14,
    fontWeight: 700,
    color: INK,
    cursor: "pointer",
  };
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 36 }}>
      {step > 0 ? (
        <button onClick={onPrev} style={navBtnStyle}>
          ← Atrás
        </button>
      ) : (
        <span />
      )}
      {step < total - 1 && (
        <button onClick={onNext} style={{ ...navBtnStyle, background: INK, color: PARCHMENT_TEXT }}>
          Siguiente →
        </button>
      )}
    </div>
  );
}

function DottedRule() {
  return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 5 }}>
      <div style={{ flex: 1, borderTop: `1px dotted ${INK_MUTED}` }} />
      <svg width="14" height="9" viewBox="0 0 14 9" style={{ flexShrink: 0 }}>
        <path d="M0,4.5 L5.5,1 L5.5,8 Z" fill={INK_MUTED} />
        <path d="M14,4.5 L8.5,1 L8.5,8 Z" fill={INK_MUTED} />
        <circle cx="7" cy="4.5" r="1.5" fill={INK_MUTED} />
      </svg>
      <div style={{ flex: 1, borderTop: `1px dotted ${INK_MUTED}` }} />
    </div>
  );
}

function Flourish() {
  return (
    <svg viewBox="0 0 200 20" style={{ width: 150, height: 15, display: "block", margin: "2px auto 22px" }}>
      <path
        d="M0,10 C28,1 42,19 68,10 C82,5 92,15 100,10 C108,5 118,15 132,10 C158,19 172,1 200,10"
        fill="none"
        stroke={INK_MUTED}
        strokeWidth="1.1"
      />
      <circle cx="100" cy="10" r="2.6" fill={INK_MUTED} />
    </svg>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ margin: "18px 0" }}>
      <p style={{ fontFamily: VINTAGE_FONT, fontStyle: "italic", fontSize: 18, color: INK_MUTED, margin: 0, textAlign: "center" }}>{title}</p>
      {children}
    </div>
  );
}

const DICTATION_MESSAGES = {
  error: "No pudimos pasar el audio a texto. Probá de nuevo o escribila a mano.",
  denied: "Necesitamos permiso para usar el micrófono. Habilitalo en tu navegador y volvé a intentar.",
  empty: "No llegamos a escuchar nada. Probá hablando un poco más cerca del micrófono.",
};

function DictationControl({ status, seconds, onStart, onStop }) {
  const recording = status === "recording";
  const transcribing = status === "transcribing";
  const buttonStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "none",
    border: `1px solid ${INK_MUTED}88`,
    borderRadius: 2,
    padding: "6px 14px",
    fontFamily: VINTAGE_FONT,
    fontStyle: "italic",
    fontSize: 14,
    color: INK,
    cursor: transcribing ? "default" : "pointer",
  };

  return (
    <div style={{ textAlign: "center", margin: "10px 0 4px" }}>
      {recording ? (
        <button type="button" onClick={onStop} style={buttonStyle}>
          <span className="tl-rec-dot" style={{ width: 9, height: 9, borderRadius: "50%", background: "#9B2B1F" }} />
          Grabando {formatClock(seconds)} / {formatClock(MAX_DICTATION_SECONDS)} · Terminar
        </button>
      ) : (
        <button type="button" onClick={onStart} disabled={transcribing} style={buttonStyle}>
          <MicIcon />
          {transcribing ? "Pasando tu audio a texto…" : "Dictar la carta en voz alta"}
        </button>
      )}
      {status === "idle" && (
        <p style={{ margin: "6px 0 0", fontSize: 12.5, color: INK_MUTED, fontStyle: "italic" }}>
          Contala como te salga: la pasamos a texto y después la podés retocar.
        </p>
      )}
      {DICTATION_MESSAGES[status] && (
        <p style={{ margin: "6px 0 0", fontSize: 13, color: INK_MUTED, fontStyle: "italic" }}>{DICTATION_MESSAGES[status]}</p>
      )}
    </div>
  );
}

function MicIcon() {
  return (
    <svg width="12" height="16" viewBox="0 0 12 16" fill="none" stroke={INK} strokeWidth="1.3" aria-hidden="true">
      <rect x="3.5" y="0.8" width="5" height="9" rx="2.5" />
      <path d="M1 7.5a5 5 0 0 0 10 0M6 12.5v2.7M3.5 15.2h5" strokeLinecap="round" />
    </svg>
  );
}

function SectionHeader({ children }) {
  return <p style={{ textAlign: "center", fontSize: 15, fontWeight: 700, margin: "22px 0 12px" }}>{children}</p>;
}

function BigSectionHeader({ children }) {
  return <p style={{ textAlign: "center", fontSize: 19, fontWeight: 700, margin: "28px 0 12px", letterSpacing: 0.2 }}>{children}</p>;
}

function SwatchGrid({ options, value, onChange }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px 6px", marginBottom: 6 }}>
      {options.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            onClick={() => onChange(opt.id)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "4px 2px",
              fontFamily: VINTAGE_FONT,
              fontSize: 12.5,
              fontWeight: active ? 700 : 400,
              color: INK,
            }}
          >
            <span
              style={{
                width: 15,
                height: 15,
                background: opt.hex,
                border: `1px solid ${INK}77`,
                boxShadow: active ? `0 0 0 2px ${KRAFT_BASE}, 0 0 0 3px ${INK}` : "none",
                flexShrink: 0,
              }}
            />
            <span>{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function TextToggle({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: "none",
        border: "none",
        borderBottom: active ? `2px solid ${INK}` : "2px solid transparent",
        padding: "2px 2px 4px",
        cursor: "pointer",
        fontFamily: VINTAGE_FONT,
        fontSize: 14,
        fontWeight: active ? 700 : 400,
        color: INK,
      }}
    >
      {children}
    </button>
  );
}

function PostPagoScreen({ status }) {
  const contacto = (() => {
    try {
      return sessionStorage.getItem("tl_contacto") || "";
    } catch (err) {
      return "";
    }
  })();
  const messages = {
    exito: {
      title: "¡Gracias!",
      body: `Estamos confirmando tu pago. En cuanto se acredite te contactamos${
        contacto ? ` por ${contacto}` : ""
      } para arrancar con tu carta.`,
    },
    pendiente: {
      title: "Pago pendiente",
      body: "Tu pago quedó pendiente de confirmación. Te vamos a avisar apenas se acredite.",
    },
    fallo: {
      title: "Hubo un problema",
      body: "No pudimos procesar el pago. Podés volver a intentarlo cuando quieras.",
    },
  };
  const msg = messages[status] || messages.fallo;
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", textAlign: "center" }}>
      <h1 style={{ fontSize: 27, fontWeight: 700, margin: "0 0 14px" }}>{msg.title}</h1>
      <Flourish />
      <p style={{ fontSize: 18, color: INK_MUTED, fontStyle: "italic", lineHeight: 1.5 }}>{msg.body}</p>
    </div>
  );
}

function TransferPaymentScreen({ datosBancarios, comprobanteStatus, onUpload }) {
  const whatsappText = encodeURIComponent(
    `Hola! Te mando el comprobante de mi transferencia para el pedido de That Letter.`
  );
  return (
    <div style={{ margin: "26px 0 0" }}>
      <p style={{ textAlign: "center", fontSize: 18, fontWeight: 700, marginBottom: 14 }}>
        Transferí el total a esta cuenta
      </p>
      <div
        style={{
          border: `1px solid ${INK_MUTED}88`,
          borderRadius: 4,
          padding: "14px 16px",
          background: "rgba(0,0,0,0.035)",
          maxWidth: 320,
          margin: "0 auto",
        }}
      >
        <SummaryRow label="CBU" value={datosBancarios?.cbu || "-"} />
        {datosBancarios?.alias && <SummaryRow label="Alias" value={datosBancarios.alias} />}
        <SummaryRow label="Titular" value={datosBancarios?.titular || "-"} />
        <SummaryRow label="Banco" value={datosBancarios?.banco || "-"} />
      </div>

      <p style={{ textAlign: "center", fontSize: 13, color: INK_MUTED, fontStyle: "italic", margin: "18px 0 10px" }}>
        Mandanos el comprobante para que confirmemos tu pedido:
      </p>

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
        <label
          style={{
            border: `1.5px solid ${INK}`,
            borderRadius: 4,
            padding: "10px 20px",
            fontFamily: VINTAGE_FONT,
            fontSize: 14,
            fontWeight: 700,
            color: INK,
            cursor: "pointer",
          }}
        >
          {comprobanteStatus === "uploading" ? "Subiendo..." : comprobanteStatus === "uploaded" ? "Comprobante subido ✓" : "Subir comprobante"}
          <input
            type="file"
            accept="image/*,application/pdf"
            style={{ display: "none" }}
            disabled={comprobanteStatus === "uploading"}
            onChange={(e) => onUpload(e.target.files?.[0])}
          />
        </label>
        {comprobanteStatus === "error" && (
          <p style={{ fontSize: 12, color: "#8C2F22", fontStyle: "italic" }}>No se pudo subir, probá de nuevo.</p>
        )}
        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappText}`}
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: 13, color: INK_MUTED, textDecoration: "underline" }}
        >
          O mandalo por WhatsApp
        </a>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        borderBottom: `1px dotted ${INK_MUTED}55`,
        padding: "7px 0",
        fontSize: 13,
      }}
    >
      <span style={{ color: INK_MUTED, fontStyle: "italic", whiteSpace: "nowrap" }}>{label}</span>
      <span style={{ textAlign: "right", fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function EnvioChip({ active, onClick, label, price, discountPercent }) {
  const priceLabel = discountPercent > 0 ? `${discountPercent}% OFF` : price === 0 ? "Gratis" : `$${formatArs(price)}`;
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        width: "100%",
        boxSizing: "border-box",
        padding: "10px 8px",
        borderRadius: 4,
        border: `1px solid ${active ? INK : INK_MUTED + "66"}`,
        background: active ? INK : "transparent",
        cursor: "pointer",
        fontFamily: VINTAGE_FONT,
      }}
    >
      <span style={{ fontSize: 12, fontWeight: active ? 700 : 400, color: active ? PARCHMENT_TEXT : INK, textAlign: "center" }}>{label}</span>
      <span style={{ fontSize: 10, fontStyle: "italic", color: active ? PARCHMENT_TEXT : INK_MUTED, opacity: 0.9 }}>{priceLabel}</span>
    </button>
  );
}

function Stepper({ value, onChange, min = 0, max = 6 }) {
  const stepperBtnStyle = {
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: `1px solid ${INK_MUTED}`,
    background: "none",
    color: INK,
    fontSize: 16,
    lineHeight: 1,
    cursor: "pointer",
    fontFamily: VINTAGE_FONT,
  };
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16 }}>
      <button onClick={() => onChange(Math.max(min, value - 1))} style={stepperBtnStyle}>
        –
      </button>
      <span style={{ fontSize: 17, fontWeight: 700, minWidth: 16, textAlign: "center" }}>{value}</span>
      <button onClick={() => onChange(Math.min(max, value + 1))} style={stepperBtnStyle}>
        +
      </button>
    </div>
  );
}

function CheckRow({ checked, disabled, onChange, label, sublabel }) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 14,
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? "default" : "pointer",
      }}
    >
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} style={{ width: 15, height: 15 }} />
      <span style={{ fontWeight: checked ? 700 : 400 }}>{label}</span>
      {sublabel && <span style={{ fontSize: 11, color: INK_MUTED, fontStyle: "italic" }}>{sublabel}</span>}
    </label>
  );
}

function FlorSeca({ x, y, rotate }) {
  const petals = [0, 72, 144, 216, 288];
  const petalA = FLOR_SECA_COLOR;
  const petalB = shade(FLOR_SECA_COLOR, -18);
  const veinColor = shade(FLOR_SECA_COLOR, -35);
  return (
    <g transform={`translate(${x},${y}) rotate(${rotate})`}>
      <path d="M0,3 C-1,7 -2,10 -1,13" stroke="#77693C" strokeWidth="0.7" fill="none" opacity="0.85" />
      <path d="M-1,9 C-4,8 -6,10 -7,12" stroke="#77693C" strokeWidth="0.6" fill="none" opacity="0.8" />
      <ellipse cx="-7" cy="12" rx="2.6" ry="1.3" fill="#87914f" opacity="0.85" transform="rotate(-25 -7 12)" />
      {petals.map((angle, i) => (
        <g key={angle} transform={`rotate(${angle})`}>
          <ellipse cx="0" cy="-6.2" rx="2.4" ry="5.6" fill={i % 2 === 0 ? petalA : petalB} stroke={veinColor} strokeWidth="0.3" opacity="0.95" />
          <line x1="0" y1="-1.2" x2="0" y2="-10.8" stroke={veinColor} strokeWidth="0.25" opacity="0.7" />
          <line x1="-1" y1="-3" x2="0.4" y2="-7" stroke={veinColor} strokeWidth="0.18" opacity="0.5" />
          <line x1="1" y1="-3" x2="-0.4" y2="-7" stroke={veinColor} strokeWidth="0.18" opacity="0.5" />
        </g>
      ))}
      <circle r="2.3" fill="#8a6a2e" stroke={shade("#8a6a2e", -25)} strokeWidth="0.3" />
      <circle r="0.9" fill={shade("#8a6a2e", 30)} opacity="0.7" />
    </g>
  );
}

function Estampilla({ x, y, rotate, envelopeHex }) {
  const w = 46;
  const h = 58;
  const perf = perforationPoints(w, h, 7.4);
  return (
    <g transform={`translate(${x},${y}) rotate(${rotate})`}>
      <rect x={-w / 2} y={-h / 2} width={w} height={h} fill={STAMP_COLOR} stroke={shade(STAMP_COLOR, -25)} strokeWidth="0.6" />
      <rect
        x={-w / 2 + 4}
        y={-h / 2 + 4}
        width={w - 8}
        height={h - 8}
        fill="none"
        stroke={shade(STAMP_COLOR, 45)}
        strokeWidth="0.7"
        opacity="0.8"
      />
      {perf.map(([px, py], i) => (
        <circle key={i} cx={px} cy={py} r="2.1" fill={envelopeHex} stroke={shade(STAMP_COLOR, -30)} strokeWidth="0.25" />
      ))}
      <circle cx="0" cy="-4" r="8.5" fill="none" stroke={shade(STAMP_COLOR, 45)} strokeWidth="0.7" opacity="0.85" />
      <text
        x="0"
        y="-4"
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="'Times New Roman', Times, serif"
        fontWeight="700"
        fontSize="9"
        letterSpacing="0.5"
        fill={shade(STAMP_COLOR, 55)}
      >
        TL
      </text>
      <text
        x="0"
        y="14"
        textAnchor="middle"
        fontFamily="'Times New Roman', Times, serif"
        fontSize="4.6"
        letterSpacing="1.2"
        fill={shade(STAMP_COLOR, 45)}
        opacity="0.9"
      >
        CORREO
      </text>
      <text
        x="0"
        y="22"
        textAnchor="middle"
        fontFamily="'Times New Roman', Times, serif"
        fontSize="4.2"
        fill={shade(STAMP_COLOR, 45)}
        opacity="0.85"
      >
        ARGENTINA
      </text>
    </g>
  );
}

function PolaroidStack({ count }) {
  const photos = Array.from({ length: Math.min(count, 4) });
  const tones = ["#8FA37E", "#C79A8C", "#8FA0B8", "#B79AC7"];
  return (
    <g>
      {photos.map((_, i) => (
        <g key={i} transform={`translate(${46 + i * 6},${58 + i * 5}) rotate(${-14 + i * 8})`}>
          <rect x="-17" y="-20" width="34" height="40" rx="1.5" fill="#F7F1E4" stroke="#00000030" strokeWidth="0.6" />
          <rect x="-13" y="-16" width="26" height="24" fill={tones[i % tones.length]} opacity="0.88" />
          <rect x="-13" y="-16" width="26" height="24" fill="#000" opacity="0.06" />
        </g>
      ))}
    </g>
  );
}

function EnvelopePreview({ envelopeHex, closureType, closureHex, estampillas, florSeca, lazo, dedication, polaroidPacks }) {
  const fold = shade(envelopeHex, -22);
  const foldLight = shade(envelopeHex, envelopeHex === "#FBF8F1" ? -12 : 12);
  const waxHighlight = shade(WAX_COLOR, 45);
  const waxShadow = shade(WAX_COLOR, -30);
  const darkEnvelopes = ["#1C1A18", "#1F2A4A", "#5A2C86", "#6E1B26"];
  const textInk = darkEnvelopes.includes(envelopeHex) ? "#F1E8D2" : INK;

  return (
    <svg viewBox="0 0 360 250" style={{ width: "100%", maxWidth: 360, display: "block", margin: "0 auto" }}>
      <defs>
        <filter id="paperGrain" x="-5%" y="-5%" width="110%" height="110%">
          <feTurbulence type="fractalNoise" baseFrequency="0.75" numOctaves="2" stitchTiles="stitch" result="noise" />
          <feColorMatrix in="noise" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.18 0" />
        </filter>
        <filter id="envelopeShadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="4" stdDeviation="5" floodColor="#000000" floodOpacity="0.3" />
        </filter>
        <clipPath id="envelopeClip">
          <rect x="10" y="24" width="340" height="200" rx="3" />
        </clipPath>
      </defs>

      <g filter="url(#envelopeShadow)">
        <rect x="10" y="24" width="340" height="200" rx="3" fill={envelopeHex} stroke={shade(envelopeHex, -35)} strokeWidth="1.5" />
        <path d="M10,24 L180,124 L10,224 Z" fill={foldLight} opacity="0.32" />
        <path d="M350,24 L180,124 L350,224 Z" fill={foldLight} opacity="0.32" />
        <path d="M10,24 L180,124 L350,24 Z" fill={fold} stroke={shade(envelopeHex, -35)} strokeWidth="1.5" />

        <rect x="10" y="24" width="340" height="200" clipPath="url(#envelopeClip)" filter="url(#paperGrain)" style={{ mixBlendMode: "multiply" }} />

        {closureType === "cinta" && (
          <g>
            <rect x="160" y="24" width="40" height="200" fill={closureHex} opacity="0.92" />
            <g transform="translate(180,122)" fill={shade(closureHex, -22)}>
              <path d="M0,0 C-5,-12 -18,-13 -21,-3 C-23,4 -14,10 -5,5 C-2,3.5 -1,1.5 0,0 Z" />
              <path d="M0,0 C5,-12 18,-13 21,-3 C23,4 14,10 5,5 C2,3.5 1,1.5 0,0 Z" />
              <path d="M-2,3 C-5,8 -8,13 -13,17 C-10,14 -6,10 -2,7 Z" />
              <path d="M2,3 C5,8 8,13 13,17 C10,14 6,10 2,7 Z" />
              <ellipse cx="0" cy="1" rx="2.8" ry="2.1" fill={shade(closureHex, -34)} />
            </g>
          </g>
        )}

        {closureType === "lacre" && (
          <g>
            {lazo && (
              <g>
                <rect x="172" y="24" width="16" height="200" fill={LAZO_COLOR} opacity="0.92" />
                <rect x="178" y="24" width="4" height="200" fill={shade(LAZO_COLOR, -25)} opacity="0.4" />
              </g>
            )}

            <path d={wavyCirclePath(180, 122, 23, 14, 2.2)} fill={WAX_COLOR} stroke={waxShadow} strokeWidth="1" />
            <circle cx="180" cy="122" r="13" fill={waxHighlight} opacity="0.35" />
            <text
              x="180"
              y="122"
              textAnchor="middle"
              dominantBaseline="central"
              fontFamily="'Times New Roman', Times, serif"
              fontWeight="700"
              fontSize="15"
              letterSpacing="1"
              fill={waxShadow}
              opacity="0.9"
            >
              TL
            </text>

            {florSeca && <FlorSeca x={168} y={131} rotate={-12} />}
          </g>
        )}

        {estampillas && <Estampilla x={310} y={56} rotate={-6} envelopeHex={envelopeHex} />}

        {polaroidPacks > 0 && <PolaroidStack count={polaroidPacks} />}

        {dedication.trim() && (
          <text x="30" y="209" fontFamily="'Times New Roman', Times, serif" fontStyle="italic" fontSize="14" fill={textInk}>
            Para: {dedication}
          </text>
        )}
      </g>
    </svg>
  );
}
